import type { Creature } from '@/domain/creature/types';
import type { Genome } from '@/domain/genetics/types';
import type { NpcCreature } from './types';
import { computeStage } from '@/domain/lifecycle';
import { NPC_TIME_SCALE } from '@/domain/care/constants';

/**
 * `tickNpc` recomputa a idade do NPC a partir de `bornAt` com
 * `(nowMs - bornAt) * NPC_TIME_SCALE`. Se guardássemos o `bornAt` real da
 * criatura, o primeiro tick pós-aposentadoria "rejuvenesceria" o NPC (a idade
 * escalada é só 25% da real). Guardamos em vez disso um `bornAt` ajustado tal
 * que a fórmula escalada, avaliada em `retiredAt`, reproduza exatamente a
 * idade real na aposentadoria — sem salto de estágio no tick seguinte.
 */
function effectiveBornAt(bornAt: number, retiredAt: number): number {
  return retiredAt - (retiredAt - bornAt) / NPC_TIME_SCALE;
}

export function npcFromCreature(
  c: Creature,
  retiredAt: number,
  parentIds: [string | null, string | null] = [null, null],
): NpcCreature {
  return {
    id: c.id,
    name: c.name,
    genome: c.genome,
    bornAt: effectiveBornAt(c.lifecycle.bornAt, retiredAt),
    retiredAt,
    stage: c.lifecycle.stage,
    elderSince: c.lifecycle.elderSince,
    dead: c.state.dead,
    diedAt: c.state.dead ? retiredAt : null,
    causeOfDeath: c.state.causeOfDeath,
    parentIds,
    careFinalScore: c.lifecycle.careFinalScore,
  };
}

export interface NpcFromGenomeInput {
  id: string;
  name: string;
  bornAt: number;
  retiredAt: number;
  parentIds: [string | null, string | null];
  genome: Genome;
}

export function npcFromGenome(input: NpcFromGenomeInput): NpcCreature {
  const bornAt = effectiveBornAt(input.bornAt, input.retiredAt);
  const stage = computeStage((input.retiredAt - bornAt) * NPC_TIME_SCALE);
  return {
    id: input.id,
    name: input.name,
    genome: input.genome,
    bornAt,
    retiredAt: input.retiredAt,
    stage,
    elderSince: stage === 'elder' ? input.retiredAt : null,
    dead: false,
    diedAt: null,
    causeOfDeath: null,
    parentIds: input.parentIds,
    careFinalScore: null,
  };
}
